/**
 * Currency Detection Utilities
 *
 * Provides intelligent currency field detection using attribute metadata patterns
 * instead of relying solely on hardcoded keyword matching.
 */

import type { Attribute } from "../types";

/**
 * Normalizes control/display names for robust comparison
 * Removes all non-alphanumeric characters and converts to lowercase
 *
 * @param s - String to normalize
 * @returns Normalized string with only lowercase alphanumeric characters
 *
 * @example
 * normalizeName("Parent Income") // returns "parentincome"
 * normalizeName("Student's GPA") // returns "studentsgpa"
 */
export function normalizeName(s?: string): string {
  if (!s) return "";
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * Determines if a control should be formatted as currency
 *
 * Uses a metadata-driven approach with multiple detection patterns:
 * 1. Explicit dataType indicators (currency, money)
 * 2. Numeric range patterns (large max values typical of currency fields)
 * 3. possibleValues patterns (currency symbols in dropdown options)
 * 4. Step patterns (0.01 or 1 with large max values)
 * 5. Description text analysis (explicit money/currency mentions)
 *
 * IMPORTANT: This function requires the `attribute` parameter with metadata
 * for accurate detection. Without metadata, it returns false.
 *
 * @param controlName - Name of the control field (used only as fallback identifier)
 * @param attribute - Attribute object with metadata (REQUIRED for detection)
 * @returns true if field should be formatted as currency
 *
 * @example
 * // With full attribute metadata (recommended)
 * isFinancialControl("Income", {
 *   name: "Parent Income",
 *   dataType: "Number",
 *   possibleMaxValue: 2000000,
 *   possibleMinValue: 0
 * }) // returns true
 *
 * // Without metadata - no detection possible
 * isFinancialControl("Parent Income") // returns false
 */
export function isFinancialControl(
  controlName: string,
  attribute?: Partial<Attribute> & {
    name?: string;
    dataType?: string;
    [key: string]: any;
  },
): boolean {
  // If full attribute is provided, use metadata-based detection
  if (attribute) {
    // Check 1: Explicit dataType indicator (if backend ever adds it)
    if (
      attribute.dataType?.toLowerCase() === "currency" ||
      attribute.dataType?.toLowerCase() === "money"
    ) {
      return true;
    }

    // Check 2: possibleMaxValue patterns suggest currency
    // Financial fields often have large numeric ranges (e.g., $0 - $2,000,000)
    const maxVal = attribute.possibleMaxValue;
    const minVal = attribute.possibleMinValue;

    // Pattern 2a: Large numeric max value (>= $100k is typically currency)
    if (typeof maxVal === "number" && maxVal >= 100000) {
      return true;
    }

    // Pattern 2b: String max value with currency symbols or large number words
    if (typeof maxVal === "string") {
      const hasMoneyIndicator = /[$€£¥₹]|million|thousand|,\d{3}/i.test(maxVal);
      if (hasMoneyIndicator) {
        return true;
      }

      // Parse numeric value from string like "2000000" or "2,000,000"
      const numericStr = maxVal.replace(/[^\d]/g, "");
      const numericMax = Number.parseInt(numericStr, 10);
      if (!Number.isNaN(numericMax) && numericMax >= 100000) {
        return true;
      }
    }

    // Pattern 2c: Min is 0 and max is large = typical financial range pattern
    if (
      (minVal === 0 || minVal === "0") &&
      typeof maxVal === "number" &&
      maxVal >= 50000
    ) {
      return true;
    }

    // Check 3: possibleValues patterns (dropdown with currency-formatted options)
    if (attribute.possibleValues) {
      const valuesStr = String(attribute.possibleValues);
      // Look for currency symbols or large comma-separated numbers
      if (/\$\d+|£\d+|€\d+|\d{1,3},\d{3}/i.test(valuesStr)) {
        return true;
      }
    }

    // Check 4: Numeric dataType with step patterns (currency often uses 0.01 or 1 step)
    if (
      (attribute.dataType === "Number" || attribute.dataType === "Integer") &&
      attribute.possibleRangeStep !== undefined
    ) {
      const step = attribute.possibleRangeStep;
      // Currency fields often use step of 0.01 (cents) or 1 (dollars)
      if (step === 0.01 || step === 1 || step === "0.01" || step === "1") {
        // Combined with large max value
        if (typeof maxVal === "number" && maxVal >= 10000) {
          return true;
        }
      }
    }

    return false;
  }

  // No attribute metadata provided - cannot reliably detect currency fields
  // This function requires attribute metadata for accurate currency detection
  return false;
}
